/**
 * Time utilities for Europe/Berlin timezone
 * Converts local Berlin time to ISO-8601 with proper offset
 */

/**
 * Convert Berlin local datetime string to ISO-8601 with offset
 * @param localStr - Local time in format "YYYY-MM-DDTHH:mm"
 * @returns ISO-8601 string with Berlin offset "YYYY-MM-DDTHH:mm:ss+01:00" or "+02:00"
 */
export function toISO8601Berlin(localStr: string): string {
  // Parse the local string (YYYY-MM-DDTHH:mm)
  const match = localStr.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) {
    throw new Error(`Invalid local time format: "${localStr}". Expected YYYY-MM-DDTHH:mm`);
  }

  const [, year, month, day, hour, minute] = match;

  // Create a date in local browser time (assuming browser is in Berlin timezone for consistency)
  // For production, we'd use a proper timezone library, but this works for Europe/Berlin context
  const localDate = new Date(`${year}-${month}-${day}T${hour}:${minute}:00`);

  // Check if DST is in effect for this date in Berlin
  // Berlin uses CET (UTC+1) in winter and CEST (UTC+2) in summer
  // DST typically: last Sunday of March 02:00 to last Sunday of October 03:00
  const isDST = isDSTInBerlin(localDate);
  const offset = isDST ? '+02:00' : '+01:00';

  // Format as ISO-8601 with seconds and offset
  return `${year}-${month}-${day}T${hour}:${minute}:00${offset}`;
}

/**
 * Determine if a date is in DST for Europe/Berlin
 * @param date - Date object
 * @returns true if DST is active
 */
function isDSTInBerlin(date: Date): boolean {
  const year = date.getFullYear();
  const month = date.getMonth(); // 0-11
  const day = date.getDate();

  // DST runs from last Sunday of March to last Sunday of October
  // Months: 0=Jan, 2=Mar, 9=Oct, 11=Dec

  // Before March or after October: definitely not DST
  if (month < 2 || month > 9) return false;

  // April through September: definitely DST
  if (month > 2 && month < 9) return true;

  // March or October: need to check if we're past the transition Sunday
  const lastSunday = getLastSundayOfMonth(year, month);

  if (month === 2) { // March
    // DST starts at 02:00 on last Sunday of March
    return day >= lastSunday;
  } else { // October
    // DST ends at 03:00 on last Sunday of October
    return day < lastSunday;
  }
}

/**
 * Get the day of month for the last Sunday of a given month
 * @param year - Year
 * @param month - Month (0-11)
 * @returns Day of month (1-31)
 */
function getLastSundayOfMonth(year: number, month: number): number {
  // Start from the last day of the month and work backwards
  const lastDay = new Date(year, month + 1, 0); // Day 0 of next month = last day of this month
  const lastDayNum = lastDay.getDate();
  const lastDayOfWeek = lastDay.getDay(); // 0=Sunday

  // Calculate how many days to subtract to get to the last Sunday
  const daysToSubtract = lastDayOfWeek; // If lastDay is Sunday (0), subtract 0; if Monday (1), subtract 1, etc.

  return lastDayNum - daysToSubtract;
}

/**
 * Add minutes to a Berlin local datetime string
 * @param localStr - Local time in format "YYYY-MM-DDTHH:mm"
 * @param minutes - Minutes to add
 * @returns New local datetime string in same format
 */
export function addMinutes(localStr: string, minutes: number): string {
  const match = localStr.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) {
    throw new Error(`Invalid local time format: "${localStr}". Expected YYYY-MM-DDTHH:mm`);
  }

  const [, year, month, day, hour, minute] = match;
  const date = new Date(`${year}-${month}-${day}T${hour}:${minute}:00`);
  date.setMinutes(date.getMinutes() + minutes);

  const newYear = date.getFullYear();
  const newMonth = String(date.getMonth() + 1).padStart(2, '0');
  const newDay = String(date.getDate()).padStart(2, '0');
  const newHour = String(date.getHours()).padStart(2, '0');
  const newMinute = String(date.getMinutes()).padStart(2, '0');

  return `${newYear}-${newMonth}-${newDay}T${newHour}:${newMinute}`;
}

/**
 * Build ISO-8601 from/to timestamps for Make webhook
 * @param start - Start time in "YYYY-MM-DDTHH:mm" format (Berlin local)
 * @param durationMinutes - Duration in minutes
 * @returns Object with from and to in ISO-8601 format with Berlin offset
 */
export function buildFromToISO(start: string, durationMinutes: number): { from: string; to: string } {
  const from = toISO8601Berlin(start);
  const endLocal = addMinutes(start, durationMinutes);
  const to = toISO8601Berlin(endLocal);

  // Validate format
  const iso8601Regex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}[+]\d{2}:\d{2}$/;
  if (!iso8601Regex.test(from)) {
    throw new Error(`Invalid from timestamp: "${from}"`);
  }
  if (!iso8601Regex.test(to)) {
    throw new Error(`Invalid to timestamp: "${to}"`);
  }

  // Validate from < to
  if (from >= to) {
    throw new Error(`Invalid time range: from="${from}" must be before to="${to}"`);
  }

  return { from, to };
}
